import { RefObject } from 'react';
import { createPortal } from 'react-dom';
import { ToastContainer, ToastContainerProps } from 'react-toastify';

interface ModalToastContainerProps extends Partial<ToastContainerProps> {
  targetRef?: RefObject<HTMLElement>;
}

export const MODAL_TOAST_CONTAINER_ID = 'modal-toast';

const topOffset = (modalRef?: React.RefObject<HTMLElement>) => {
  const el = modalRef?.current;
  if (!el) return '0px';

  const viewportHeight = window.innerHeight;
  const modalRect = el.getBoundingClientRect();
  const styles = getComputedStyle(el);

  const paddingTop = parseFloat(styles.paddingTop) || 0;
  const paddingBottom = parseFloat(styles.paddingBottom) || 0;

  const modalHeight = modalRect.height + paddingTop + paddingBottom;
  const offset = Math.max((viewportHeight - modalHeight) / 2, 0);

  return `-${offset}px`;
};

function ModalToastContainer({
  targetRef,
  position = 'top-center',
  autoClose = 5000,
  hideProgressBar = false,
  newestOnTop = false,
  closeOnClick = true,
  rtl = false,
  pauseOnFocusLoss = true,
  draggable = true,
  pauseOnHover = true,
  style = {
    minWidth: '340px',
    maxWidth: '520px',
    fontSize: '0.875rem',
    top: topOffset(targetRef),
  },
  ...props
}: ModalToastContainerProps) {
  if (!targetRef?.current) {
    return null;
  }

  return createPortal(
    <ToastContainer
      position={position}
      autoClose={autoClose}
      hideProgressBar={hideProgressBar}
      newestOnTop={newestOnTop}
      closeOnClick={closeOnClick}
      rtl={rtl}
      pauseOnFocusLoss={pauseOnFocusLoss}
      draggable={draggable}
      pauseOnHover={pauseOnHover}
      style={style}
      containerId={MODAL_TOAST_CONTAINER_ID}
      {...props}
      aria-live="polite"
      role="alert"
    />,
    targetRef.current,
  );
}

export default ModalToastContainer;
